// Homepage Content Management Tab
// Organized by landing section; Who + Differentiators use editable tables.

import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { contentManagementService, HomepageContent } from '@/services/contentManagementService';
import { toast } from 'react-hot-toast';
import {
  DEFAULT_COMPARE_ROWS,
  DEFAULT_WHO_CARDS,
  HomepageCompareRow,
  HomepageWhoCard,
  HomepageWhoImageKey,
  parseCompareRows,
  parseWhoCards,
} from './homepageCmsTypes';

type HomepageKey = HomepageContent['content_key'];

type SectionId =
  | 'hero'
  | 'approach'
  | 'impact'
  | 'cta'
  | 'who'
  | 'compare';

const SECTION_NAV: Array<{ id: SectionId; label: string }> = [
  { id: 'hero', label: '1. Hero' },
  { id: 'approach', label: '2. Approach / How' },
  { id: 'impact', label: '3. Impact' },
  { id: 'cta', label: '4. CTA' },
  { id: 'who', label: '5. Who' },
  { id: 'compare', label: '6. Differentiators' },
];

const TEXT_FIELDS: Array<{
  section: SectionId;
  key: HomepageKey;
  label: string;
  hint?: string;
  multiline?: boolean;
}> = [
  { section: 'hero', key: 'hero_title', label: 'Title' },
  { section: 'hero', key: 'hero_subtitle', label: 'Subtitle', multiline: true },
  { section: 'approach', key: 'approach_title', label: 'Our Approach — title' },
  { section: 'approach', key: 'approach_body', label: 'Our Approach — body', multiline: true },
  { section: 'approach', key: 'how_title', label: 'How we make it work — title' },
  { section: 'approach', key: 'how_body', label: 'How we make it work — body', multiline: true },
  { section: 'impact', key: 'impact_label', label: 'Badge' },
  { section: 'impact', key: 'impact_headline', label: 'Headline', multiline: true },
  { section: 'impact', key: 'impact_card_1', label: 'Card 1' },
  { section: 'impact', key: 'impact_card_2', label: 'Card 2' },
  { section: 'impact', key: 'impact_card_3', label: 'Card 3' },
  { section: 'cta', key: 'cta_email_placeholder', label: 'Email placeholder' },
  { section: 'cta', key: 'cta_get_started', label: 'Get started button' },
  { section: 'cta', key: 'cta_try_ai', label: 'Build roadmap button' },
  { section: 'who', key: 'organization_title', label: 'Section title' },
  {
    section: 'who',
    key: 'organization_subtitle',
    label: 'Section subtitle (optional / legacy)',
    hint: 'Not shown on the current compact landing.',
  },
  { section: 'compare', key: 'compare_title', label: 'Section title' },
];

const IMAGE_OPTIONS: Array<{ value: HomepageWhoImageKey; label: string }> = [
  { value: 'peFirms', label: 'PE firms' },
  { value: 'peBacked', label: 'PE-backed / public' },
  { value: 'consultants', label: 'Consultants' },
];

const inputClass =
  'w-full px-2 py-1.5 text-sm border border-[var(--color-border)] rounded bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]';

function errorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    return (
      (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
      'Request failed'
    );
  }
  return 'Request failed';
}

export const HomepageContentTab: React.FC = () => {
  const [content, setContent] = useState<Record<string, HomepageContent>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState<SectionId>('hero');
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [whoCards, setWhoCards] = useState<HomepageWhoCard[]>(DEFAULT_WHO_CARDS);
  const [compareRows, setCompareRows] = useState<HomepageCompareRow[]>(DEFAULT_COMPARE_ROWS);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      const data = await contentManagementService.getAllHomepageContent();
      const map: Record<string, HomepageContent> = {};
      const nextDrafts: Record<string, string> = {};
      data.forEach((item) => {
        map[item.content_key] = item;
        nextDrafts[item.content_key] = item.content_value || '';
      });
      setContent(map);
      setDrafts(nextDrafts);
      setWhoCards(parseWhoCards(map.who_cards?.content_value) || DEFAULT_WHO_CARDS);
      setCompareRows(parseCompareRows(map.compare_rows?.content_value) || DEFAULT_COMPARE_ROWS);
    } catch (error: unknown) {
      console.error('Error loading homepage content:', error);
      toast.error(errorMessage(error) || 'Failed to load homepage content');
    } finally {
      setLoading(false);
    }
  };

  /** Single save — all text fields, Who cards, and differentiator rows (like other CMS tabs). */
  const handleSaveContent = async () => {
    const cleanedWho = whoCards
      .map((card, index) => ({
        ...card,
        id: card.id || `who-${index + 1}`,
        title: card.title.trim(),
        descLines: card.descLines.map((d) => d.trim()).filter(Boolean),
        roles: card.roles.trim(),
      }))
      .filter((c) => c.title);

    const cleanedCompare = compareRows
      .map((row) => ({
        label: row.label.trim(),
        others: row.others.trim() || 'Sometimes',
      }))
      .filter((r) => r.label);

    if (cleanedWho.length === 0) {
      toast.error('Add at least one Who card with a title');
      return;
    }
    if (cleanedCompare.length === 0) {
      toast.error('Add at least one differentiator row');
      return;
    }

    const textUpdates = TEXT_FIELDS.map(({ key }) => {
      const value = (drafts[key] ?? content[key]?.content_value ?? '').trim();
      return { key, value };
    }).filter(({ value }) => value.length > 0);

    try {
      setSaving(true);
      await Promise.all([
        ...textUpdates.map(({ key, value }) =>
          contentManagementService.updateHomepageContent(key, value)
        ),
        contentManagementService.updateHomepageContent(
          'who_cards',
          JSON.stringify(cleanedWho)
        ),
        contentManagementService.updateHomepageContent(
          'compare_rows',
          JSON.stringify(cleanedCompare)
        ),
      ]);
      toast.success('Homepage content saved successfully');
      await loadContent();
    } catch (error: unknown) {
      console.error('Error saving homepage content:', error);
      toast.error(errorMessage(error) || 'Failed to save homepage content');
    } finally {
      setSaving(false);
    }
  };

  const sectionFields = useMemo(
    () => TEXT_FIELDS.filter((f) => f.section === activeSection),
    [activeSection]
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--color-accent-bg)]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-[var(--color-secondary-text)]">
        Edit public landing copy by section. CMS values are the English source on `/`; empty fields
        fall back to translations. Who cards and differentiators support add/remove in tables.
      </p>

      {/* Section navigation */}
      <div className="flex flex-wrap gap-2 border-b border-[var(--color-border)] pb-3">
        {SECTION_NAV.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setActiveSection(s.id)}
            className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
              activeSection === s.id
                ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] border-transparent font-semibold'
                : 'bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)] hover:border-[var(--color-accent-bg)]'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {SECTION_NAV.find((s) => s.id === activeSection)?.label}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Text fields for this section */}
          {sectionFields.map(({ key, label, hint, multiline }) => (
            <div key={key} className="space-y-1.5">
              <label className="block text-sm font-medium text-[var(--color-surface-text)]">
                {label}
              </label>
              {hint && <p className="text-xs text-[var(--color-secondary-text)]">{hint}</p>}
              <textarea
                value={drafts[key] ?? content[key]?.content_value ?? ''}
                onChange={(e) => setDrafts((prev) => ({ ...prev, [key]: e.target.value }))}
                className={`${inputClass} ${multiline ? 'min-h-[100px]' : 'min-h-[56px]'}`}
              />
            </div>
          ))}

          {/* Who cards table */}
          {activeSection === 'who' && (
            <div className="space-y-3 pt-2 border-t border-[var(--color-border)]">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-[var(--color-secondary-text)]">
                  Organization cards on the landing page. Add or remove rows as needed.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    setWhoCards((prev) => [
                      ...prev,
                      {
                        id: `who-${Date.now()}`,
                        title: '',
                        descLines: [''],
                        roles: '',
                        imageKey: 'peFirms',
                      },
                    ])
                  }
                >
                  Add card
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[52rem] text-sm border-collapse">
                  <thead>
                    <tr className="text-left text-[var(--color-secondary-text)]">
                      <th className="py-2 pr-2 font-semibold">Title</th>
                      <th className="py-2 pr-2 font-semibold">Bullets (one per line)</th>
                      <th className="py-2 pr-2 font-semibold">Roles</th>
                      <th className="py-2 pr-2 font-semibold">Image</th>
                      <th className="py-2 font-semibold w-20" />
                    </tr>
                  </thead>
                  <tbody>
                    {whoCards.map((card, index) => (
                      <tr key={card.id} className="align-top border-t border-[var(--color-border)]">
                        <td className="py-2 pr-2">
                          <input
                            className={inputClass}
                            value={card.title}
                            onChange={(e) =>
                              setWhoCards((prev) =>
                                prev.map((c, i) =>
                                  i === index ? { ...c, title: e.target.value } : c
                                )
                              )
                            }
                          />
                        </td>
                        <td className="py-2 pr-2">
                          <textarea
                            className={`${inputClass} min-h-[72px]`}
                            value={card.descLines.join('\n')}
                            onChange={(e) =>
                              setWhoCards((prev) =>
                                prev.map((c, i) =>
                                  i === index
                                    ? { ...c, descLines: e.target.value.split('\n') }
                                    : c
                                )
                              )
                            }
                          />
                        </td>
                        <td className="py-2 pr-2">
                          <input
                            className={inputClass}
                            value={card.roles}
                            onChange={(e) =>
                              setWhoCards((prev) =>
                                prev.map((c, i) =>
                                  i === index ? { ...c, roles: e.target.value } : c
                                )
                              )
                            }
                          />
                        </td>
                        <td className="py-2 pr-2">
                          <select
                            className={inputClass}
                            value={card.imageKey}
                            onChange={(e) =>
                              setWhoCards((prev) =>
                                prev.map((c, i) =>
                                  i === index
                                    ? {
                                        ...c,
                                        imageKey: e.target.value as HomepageWhoImageKey,
                                      }
                                    : c
                                )
                              )
                            }
                          >
                            {IMAGE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2">
                          <button
                            type="button"
                            className="text-sm text-[var(--color-error)] hover:underline disabled:opacity-40"
                            disabled={whoCards.length <= 1}
                            onClick={() =>
                              setWhoCards((prev) => prev.filter((_, i) => i !== index))
                            }
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Differentiator table */}
          {activeSection === 'compare' && (
            <div className="space-y-3 pt-2 border-t border-[var(--color-border)]">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-[var(--color-secondary-text)]">
                  Landing comparison table. T-MOS column is always a check; edit the capability and
                  the Others label.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    setCompareRows((prev) => [...prev, { label: '', others: 'Sometimes' }])
                  }
                >
                  Add row
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[32rem] text-sm border-collapse">
                  <thead>
                    <tr className="text-left text-[var(--color-secondary-text)]">
                      <th className="py-2 pr-2 font-semibold">Differentiator (T-MOS)</th>
                      <th className="py-2 pr-2 font-semibold">Others</th>
                      <th className="py-2 font-semibold w-20" />
                    </tr>
                  </thead>
                  <tbody>
                    {compareRows.map((row, index) => (
                      <tr key={index} className="border-t border-[var(--color-border)]">
                        <td className="py-2 pr-2">
                          <input
                            className={inputClass}
                            value={row.label}
                            placeholder="e.g. Multi-tenant SaaS"
                            onChange={(e) =>
                              setCompareRows((prev) =>
                                prev.map((r, i) =>
                                  i === index ? { ...r, label: e.target.value } : r
                                )
                              )
                            }
                          />
                        </td>
                        <td className="py-2 pr-2">
                          <input
                            className={inputClass}
                            value={row.others}
                            placeholder="Mostly no / Sometimes / Rare"
                            onChange={(e) =>
                              setCompareRows((prev) =>
                                prev.map((r, i) =>
                                  i === index ? { ...r, others: e.target.value } : r
                                )
                              )
                            }
                          />
                        </td>
                        <td className="py-2">
                          <button
                            type="button"
                            className="text-sm text-[var(--color-error)] hover:underline disabled:opacity-40"
                            disabled={compareRows.length <= 1}
                            onClick={() =>
                              setCompareRows((prev) => prev.filter((_, i) => i !== index))
                            }
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="pt-2 border-t border-[var(--color-border)]">
            <Button onClick={handleSaveContent} disabled={saving} className="w-full sm:w-auto">
              {saving ? 'Saving...' : 'Save Content'}
            </Button>
            <p className="mt-1.5 text-xs text-[var(--color-secondary-text)]">
              Saves all homepage sections (Hero through Differentiators), not only the tab you are
              viewing.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
